import { unref, nextTick } from 'vue';
import { defineStore } from 'pinia';
import { router } from '@/router';
import { getSysConfig, login, rbacAdminGroup } from '@/service';
import { useRouterPush } from '@/composables';
import { localStg } from '@/utils';
import { useTabStore } from '../tab';
import { useRouteStore } from '../route';
import { useLockscreenStore } from '../lock-screen';
import { getSystemTitle, getToken, getUserInfo, getWatermark, clearAuthStorage } from './helpers';

interface AuthState {
  /** 系统标题 */
  systemTitle: string;
  /** 用户token */
  token: string;
  /** 用户信息 */
  userInfo: ApiAccount.AdminUserInfo;
  /** 水印 */
  watermark: string;
  /** 权限组 */
  perms: string[];
}

export const useAuthStore = defineStore('auth-store', {
  state: (): AuthState => ({
    systemTitle: getSystemTitle(),
    token: getToken(),
    userInfo: getUserInfo(),
    watermark: getWatermark(),
    perms: []
  }),
  getters: {
    /** 是否登录 */
    isLogin(state) {
      return Boolean(state.token);
    }
  },
  actions: {
    /** 重置auth状态 */
    resetAuthStore() {
      const { toLogin } = useRouterPush(false);
      const { resetTabStore } = useTabStore();
      const { resetRouteStore } = useRouteStore();
      const { resetLockScreenStore } = useLockscreenStore();
      const route = unref(router.currentRoute);

      clearAuthStorage();
      this.$reset();

      if (route.meta.requiresAuth) {
        toLogin();
      }

      nextTick(() => {
        resetTabStore();
        resetRouteStore();
        resetLockScreenStore();
      });
    },

    /** 登录前 */
    async beforeLogin() {
      const { data } = await getSysConfig();
      if (data) {
        // 站点名称
        if (data.name) {
          this.systemTitle = data.name;
          localStg.set('systemTitle', data.name);
        }
        // 水印
        if (data.watermark) {
          this.watermark = data.watermark;
          localStg.set('watermark', data.watermark);
        }
      }
    },

    /** 登录开始 */
    async login(params: ApiAuth.LoginParams) {
      const { error, data } = await login(params);
      if (error) {
        return Promise.reject(error);
      }
      if (data.token) {
        this.token = data.token;
        localStg.set('token', data.token);
        this.setUserInfo(data.user);
      }
      if (data.authenticator) {
        return { authenticator: data.authenticator };
      }
      return {};
    },

    /** 登录成功之后, 获取权限路由 */
    async afterLogin() {
      const { error, data } = await rbacAdminGroup();
      if (error) {
        return Promise.reject(error);
      }
      const { menus, permission } = data;
      this.perms = permission;

      return { menus, permission };
    },

    /** 保存用户数据 */
    setUserInfo(info: ApiAccount.AdminUserInfo) {
      this.userInfo = info ?? {};
      localStg.set('userInfo', info);
    }
  }
});
